import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkUrlExist } from '@api/api';
import {
  searchEaPdfUrl,
  saveSignBothData,
  changeEaState,
  completeEaProcess,
  searchMobileTerms,
  checkCertAgree,
  saveEaJsonData,
  changePlanStatus,
} from '@store/actions/eaAction';
import EaHeader from '@components/ea/EaHeader';
import EaComplete from '@components/ea/EaComplete';
import EaBottom from '@components/ea/EaBottom';
import MobileTerms from '@components/ea/MobileTerms';
import SatModal from '@components/common/satUI/SatModal';
import SatButton from '@components/common/satUI/SatButton';
import Lottie from 'react-lottie-light';
import EaLottie from '@components/ea/EaLottie.json';
import {
  NBS_BIZ_CODE_INFO,
  PLAN_PROGRESS_STATUS_INFO,
} from '@common/constants';

const EaCompleteMain = props => {
  const dispatch = useDispatch();
  const {
    applicationNo,
    complete,
    completePage,
    setEaMainPopup,
    setIsUpdatePage,
    setCurrentPage,
    setIsMobileCert,
    isPartSub = false, // 부분재발행 여부
    isConstractChange, // 모바일 청약변경(청약변경신청) 여부
    republishType, // 재발행 타입
    republishYn, // 재발행 여부
  } = props;

  const {
    pdfPath,
    signData,
    planMainInfo,
    mobileTermUrl,
    contract,
    beneficiary,
  } = useSelector(state => state.eaReducer);

  const { changeOrientation, isPortrait } = $hooks.useDevice();
  const { error: toastError } = $hooks.useToast();
  const { alert } = $hooks.useModal();
  const { saveEaToNbs } = $hooks.useApi();
  const { checkUrlExist } = $hooks.useUtil();

  const [isTabStatus, setIsTabStatus] = useState(false);
  const [signPopupVisible, setSignPopupVisible] = useState(false);
  const [completePopup, setCompletePopup] = useState(false);

  const [notCertMsg, setNotCertMsg] = useState('');
  const [isNotCertPopup, setIsNotCertPopup] = useState(false);

  const [isLottiePopup, setIsLottiePopup] = useState(false);

  const [sec, setSec] = useState(10);

  const { confirm } = $hooks.useModal();

  const lottieOptions = {
    animationData: EaLottie,
    loop: true,
    autoplay: true,
    rendererSettings: {
      className: 'add-class', // svg에 적용
      // preserveAspectRatio: 'xMidYMid slice',
    },
  };

  useEffect(() => {
    handleCertAgreeCheck(); // 청약완료화면 진입 시 수익자 동의여부 체크
    setIsUpdatePage('');
  }, [applicationNo]);

  // 성명, 서명 서버 저장
  const handleSaveSignature = (
    signNameData,
    signatureData,
    reportId,
    signId,
    nameId,
  ) => {
    dispatch(
      saveSignBothData({
        applicationNo: applicationNo,
        signNameData: signNameData,
        signatureData: signatureData,
        signGubun: 'P',
        reportId,
        signId,
        nameId,
        callback: callbackSuccess,
      }),
    );
    if (isPartSub)
      dispatch(
        searchEaPdfUrl({
          applicationNo: applicationNo,
          schedulerType: isConstractChange ? 'PENDING' : 'PREVIEW',
          callback: searchEaPdfUrlCallback,
        }),
      );
  };

  const searchEaPdfUrlCallback = type => {
    if (type) {
      !mobileTermUrl &&
        dispatch(
          searchMobileTerms({
            applicationNo: applicationNo,
            sendAlimTalkYn: 'Y',
          }),
        );
    }
  };

  const callbackSuccess = type => {
    // json data save
    if (type) {
      const completePages = [...completePage, !isPartSub ? 11 : 4];
      dispatch(
        changeEaState({
          type: 'completePage',
          data: [...new Set(completePages)],
        }),
      );

      if (!isPartSub) {
        dispatch(
          saveEaJsonData({
            applicationNo: applicationNo,
          }),
        );
      }

      setIsTabStatus(true);
      setSignPopupVisible(false);
    }
  };

  // 청약완료 버튼
  const handleSignBtnClick = async () => {
    // PDF 생성이 안 됐을 경우 청약 완료 불가
    const pdfUrl = pdfPath ? $utils.addGatewayHost(pdfPath) : '';
    const isPdfAvailable = await checkUrlExist(pdfUrl);

    if (!isPdfAvailable) {
      confirm({
        message: 'PDF 생성에 실패하였습니다.\n다시 시도하시겠습니까?',
        onClose: isOk => {
          if (isOk) makeFinalPdf();
        },
      });
      return;
    }

    // 청약 완료
    if (isTabStatus) {
      // 미결보완인 경우 신계약 청약 저장 API 호출하지 않음
      const isUndecidedPlan = $utils.checkIsUndecidedPlan(republishYn);
      if (!isUndecidedPlan) {
        // 신계약 청약 저장 API 전송 후 다음으로 이동
        const isNextAvailable = await handleSaveEaToNbs();
        if (!isNextAvailable) return;
      }

      // DocDataJson Status 변경
      await new Promise(resolve => {
        dispatch(
          completeEaProcess({
            applicationNo: applicationNo,
            docType: isConstractChange ? '5' : '1',
            republishType,
            callback: resolve,
          }),
        );
      });

      // Lottie Animation 노출
      setIsLottiePopup(true);

      // 청약 완료 상태변경 API 호출
      dispatch(
        changePlanStatus({
          applicationNo,
          status: PLAN_PROGRESS_STATUS_INFO.eaCompleted,
          republishType,
        }),
      );
    } else {
      setSignPopupVisible(true);
    }
  };

  // 개인정보신용동의여부 체크
  const handleCertAgreeCheck = () => {
    const { R10000, R10001, R10002, R10050 } = beneficiary;

    // 개인정보신용동의 처리 여부 체크
    const deathBeneficiary = R10000.map(data => {
      if (data.custNo && data.custNo !== planMainInfo.ownerPrspInfo.custNo) {
        return {
          custNo: data.custNo,
          policyRelatedPersonSubCode: '10000',
        };
      }
    });
    const injuryBeneficiary = R10001.map(data => {
      if (data.custNo && data.custNo !== planMainInfo.ownerPrspInfo.custNo) {
        return {
          custNo: data.custNo,
          policyRelatedPersonSubCode: '10001',
        };
      }
    });
    const maturityBeneficiary = R10002.map(data => {
      if (data.custNo && data.custNo !== planMainInfo.ownerPrspInfo.custNo) {
        return {
          custNo: data.custNo,
          policyRelatedPersonSubCode: '10002',
        };
      }
    });
    const proxyClaimant = R10050.map(data => {
      if (data.custNo && data.custNo !== planMainInfo.ownerPrspInfo.custNo) {
        return {
          custNo: data.custNo,
          policyRelatedPersonSubCode: '10050',
        };
      }
    });
    const sendArr = deathBeneficiary
      .concat(injuryBeneficiary, maturityBeneficiary, proxyClaimant)
      .filter(data => data !== undefined);

    if (sendArr.length > 0) {
      dispatch(
        checkCertAgree({
          applicationNo: applicationNo,
          relations: sendArr,
          callback: certAgreeCheckCallback,
        }),
      );
    } else {
      saveAndProceed();
    }
  };

  // 개인정보신용동의 완료 callback
  const certAgreeCheckCallback = (isAllAgree, notCertCust) => {
    if (isAllAgree) {
      saveAndProceed();
    } else {
      // 일부 동의 or 미동의
      setNotCertMsg(`계약관계인 ${notCertCust} 님의`);
      setIsNotCertPopup(true);
    }
  };

  const makeFinalPdf = async () => {
    dispatch(
      searchEaPdfUrl({
        applicationNo: applicationNo,
        schedulerType: isConstractChange ? 'PENDING' : 'PREVIEW',
        callback: searchEaPdfUrlCallback,
      }),
    );
  };

  const saveAndProceed = async () => {
    dispatch(
      saveEaJsonData({
        applicationNo: applicationNo,
        callback: makeFinalPdf,
      }),
    );
  };

  const handleSaveEaToNbs = async () => {
    try {
      const eaDocDataJson = $utils.makeEaDocDataJson();
      const nbsPayload = $utils.getSaveEaToNbsPayload(eaDocDataJson);

      const payload = {
        ...nbsPayload,

        omniYn: 'N', // 청약진행업무구분코드 (옴니(Y)/전자청약(N))
        applNo: applicationNo, // 발행번호
        applReqBzTpCd: NBS_BIZ_CODE_INFO.complete, // 청약요청업무구분코드
        progressStep: PLAN_PROGRESS_STATUS_INFO.eaCompleted, // 전자청약 완료 Flag ('R') 전송
      };

      const response = await saveEaToNbs({ payload });
      const isSuccess = $utils.checkApiSuccess(response);
      if (!isSuccess) throw new Error('신계약 청약 저장 API 실패');

      // API 성공 한 경우, 진행 가능여부 체크 및 보여줄 메시지 확인
      const isNbsSuccess = $utils.checkNbsSuccess(response);
      const message = $utils.getNbsMessage(response);

      // 보여줄 메시지가 있을 경우 노출
      if (message) {
        await new Promise(resolve => alert({ message, onClose: resolve }));
      }

      // 만약 신계약에서 API 결과가 성공으로 내려오지 않았는데,
      // 보여줄 메시지도 안내려왔다면 커스텀 오류 메시지 노출
      else if (!isNbsSuccess)
        toastError(
          '청약 저장 결과가 정상적이지 않습니다.\n다음에 다시 시도해주세요.',
        );

      return isNbsSuccess;
    } catch (error) {
      console.log('handleSaveEaToNbs() - error', error);
      toastError('청약 저장에 실패하였습니다.');
      return false;
    }
  };

  // 개인정보동의 미완료 버튼
  const handleNotCertBtnClick = () => {
    setIsMobileCert(true);
    setCurrentPage(isConstractChange ? 3 : 4);
  };

  // 접수 완료 버튼
  const handleCompleBtnClick = () => {
    setCompletePopup(false);
    changeOrientation('landscape'); // 가로 전환
    setTimeout(() => setEaMainPopup(false), 1000);

    // Robin: 모바일 등 외부에서 팝업을 연 경우를 위해 팝업 닫힘 메시지 전달
    $utils.sendMessageToParent({ type: 'closePopup' }, '*');
  };

  const completeCallback = type => {
    if (type) {
      setIsLottiePopup(false);
      setSec(10);
      setTimeout(() => setCompletePopup(true), 1000);
    }
  };

  // 로띠 팝업 10초 뒤 팝업 닫치게
  useEffect(() => {
    if (isLottiePopup) {
      const countdown = setInterval(() => {
        if (sec > 0) {
          setSec(sec - 1);
        }

        if (sec === 0) {
          completeCallback(true);
          clearInterval(countdown);
        }
      }, 1000);

      return () => clearInterval(countdown);
    }
  }, [isLottiePopup, sec]);

  return (
    <>
      <EaHeader
        title={
          isPartSub
            ? '3. 보완 완료'
            : republishYn
            ? '4. 계좌확인 및 보완 완료 - 보완 완료'
            : '4. 계좌등록 및 청약 완료 - 청약 완료'
        }
      />
      {pdfPath && mobileTermUrl && (
        <EaComplete
          isTabStatus={isTabStatus}
          setIsTabStatus={setIsTabStatus}
          republishYn={republishYn}
          url={
            pdfPath
            // .replace('https://ozc.prudential.co.kr', '/gateway/fileRoute')
            // .replace('http://ozc.prudential.co.kr', '/gateway/fileRoute')
          }
          mobileUrl={mobileTermUrl}
          // mobileUrl={
          //   '/getattachment/cd2caff8-e701-4a5a-a6fb-2ab939be873c/03_4110(20220101)'
          // }
        />
      )}

      <SatModal
        visible={signPopupVisible}
        setVisible={() => {
          setSignPopupVisible(false);
        }}
        type="info"
        wide
      >
        <MobileTerms
          signData={signData.EAP_ReceiptConfirmation}
          onSave={handleSaveSignature}
          complete={complete}
          ownerName={planMainInfo.ownerPrspInfo.name}
        />
      </SatModal>
      <SatModal
        visible={completePopup}
        setVisible={() => {
          setCompletePopup(false);
        }}
        type="info"
        wide
        hideCloseButton
        render={() => {
          return (
            <>
              <header className="modal-header">
                <h3 className="layer-tit2">
                  {!republishYn ? `청약 접수 완료` : `보완 접수 완료`}
                </h3>
              </header>
              <div className="cont modal-scroll-wrap">
                <p className="text-con text-center">
                  {`${
                    planMainInfo.ownerPrspInfo.name
                  } 고객님을 위한 맞춤형 보험의 ${
                    !republishYn ? `청약 접수` : `보완 접수`
                  }가 완료되었습니다.`}
                  <br />
                  {`KB라이프생명의 고객이 되어주셔서 감사드립니다.`}
                </p>
                <ul className="tock-step tock2">
                  <li className="step01">
                    {`알림톡 수신`}
                    <em>
                      {`작성하신 청약서는 알림톡(SMS)을 통하여 전달해 드리고
                      있습니다.`}
                    </em>
                  </li>
                  {((republishYn && !isPartSub) || isConstractChange) && (
                    // 전체재발행 or 청약변경신청서에 노출
                    <li className="step06">
                      {`보험료 안내사항`}
                      <ul className="dot-type mgt-5">
                        <li>
                          차액 보험료 발생 시 차회 보험료로 사용되며, 요청 시
                          신계약 초회보험료 출금계좌로 반환하여드립니다.
                        </li>
                        <li className="mgt-0">
                          추가 보험료 발생 시 별도 일회성 가상계좌로 송금하시기
                          바랍니다.
                        </li>
                      </ul>
                    </li>
                  )}
                  {!republishYn && (
                    <li className="step06">
                      {`보험료 이체`}
                      <em>
                        {`보고서 작성 후 보험료 이체가 시작됩니다.`}
                        <br />
                        {`보험료 이체가 실패할 경우 보험청약이 자동으로 취소됩니다.`}
                      </em>
                    </li>
                  )}
                </ul>
              </div>
              <div className="layer-footer">
                <SatButton
                  styleClass="btn-s blue btn-wide"
                  label={'확인'}
                  onClick={() => handleCompleBtnClick()}
                />
              </div>
            </>
          );
        }}
      />
      <SatModal
        visible={isNotCertPopup}
        setVisible={() => {
          setIsNotCertPopup(false);
        }}
        type="info"
        render={() => (
          <>
            <div className="cont">
              <p className="layer-text text-center">
                {notCertMsg}
                <br />
                {`개인(신용)정보동의가 완료되지 않았습니다.`}
                <br />
                {`고객정보확인에서 동의 완료 후`}
                <br />
                {`접수해주세요.`}
              </p>
            </div>
            <div className="layer-footer">
              <SatButton
                styleClass="btn-s blue btn-wide"
                label={'확인'}
                onClick={() => {
                  handleNotCertBtnClick();
                  setIsNotCertPopup(false);
                }}
              />
            </div>
          </>
        )}
      />
      <SatModal
        visible={isLottiePopup}
        setVisible={() => {
          setIsLottiePopup(false);
        }}
        type="lottie"
        render={() => (
          <Lottie
            options={lottieOptions}
            isStopped={false}
            isPaused={false}
            isClickToPauseDisabled={false}
            style={{ width: isPortrait ? '350px' : '440px', height: '440px' }} // svg의 부모 div에 적용
            eventListeners={[
              {
                eventName: 'complete',
              },
            ]}
          />
        )}
      />
      <EaBottom
        label={
          isTabStatus
            ? `${isPartSub ? '보완 문서' : '청약 문서'} 확인 및 ${
                republishYn ? '보완' : '청약'
              } 완료`
            : `약관 확인 및 ${republishYn ? '보완' : '청약'} 완료`
        }
        onClick={() => handleSignBtnClick()}
      />
    </>
  );
};

export default EaCompleteMain;
